import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
  type Signal,
} from '@angular/core';

import type { GroupedUsageRow } from '../../api/generated/models/GroupedUsageRow';
import { isInternalRole } from '../session/session.model';
import { SessionService } from '../session/session.service';
import { ClockService } from '../time/clock.service';
import { trailingUtcWindow } from '../time/utc-date-range';
import { FilterOptionsAdapter } from './filter-options.adapter';
import { FilterScopeService } from './filter-scope.service';
import {
  DIMENSION_STATE_SPECIFIED,
  EMPTY_OPTION_LIST,
  LOADING_OPTION_LIST,
  OPEN_SET_DIMENSIONS,
  OPTION_PROBE_WINDOW_DAYS,
  UNAVAILABLE_OPTION_LIST,
  type FilterOptionDimension,
  type FilterOptionList,
  type OpenSetDimension,
  type ResolvedDateRange,
} from './filter-state.model';

/** The client directory is per session, not per scope — one cache key for the whole session. */
const CLIENT_DIRECTORY_KEY = 'client';

/**
 * GH-164: the in-flight/controller key of the ONE cross-client fetch that settles all three
 * open-set lists. The lists themselves are cached per dimension under {@link crossClientKey}.
 */
const CROSS_CLIENT_FETCH_KEY = 'filter-options';

/** GH-164: the client-code slot of a cross-client list's key — never a valid client code. */
const CROSS_CLIENT_SLOT = '*';

/**
 * The bar's option vocabularies, cached per session (FR-015).
 *
 * Two decisions shape this service:
 *
 * **The probe window is fixed** (R-002). It is a trailing 30 UTC days computed
 * once at construction and held for the session — deliberately *not* the user's
 * selected range. Binding it to the selection would re-fetch all three lists on
 * every date interaction, contradicting FR-015 outright, and a >31-day
 * selection has no legal probe at all. The consequence is accepted explicitly:
 * for a range outside the probe window a value present in the data may be
 * missing from the dropdown — and FR-006's open-set rule already covers exactly
 * that case, since such a value stays usable via the URL and is not invalid.
 *
 * **Staff with no client selected is `error`, not empty** (R-003).
 * `/dashboard/usage` rejects a staff call that omits `clientCode` with 400, so
 * a staff caller has no legal way to ask for cross-client observed values. That
 * is the FR-016 *unavailable* state entered for a known structural reason, and
 * it is deliberately distinct from `empty`: an empty list is a truthful answer,
 * an unobtainable one is not.
 *
 * > **Amended 2026-10-29 (GH-164 S2-U1, decisions D-12).** A staff caller with no client selected
 * > now has a legal source — `GET /api/v1/dashboard/filter-options`, all three lists from one call —
 * > but it is used ONLY when the active route offers an operable open-set selector, signalled by
 * > {@link setCrossClientDemand}. Without that demand the R-003 behaviour above is unchanged,
 * > exactly: no call, the same `error` list under the same key.
 *
 * Nothing here is persisted across sessions (FR-015, explicitly disallowed).
 */
@Injectable()
export class FilterOptionsService {
  private readonly adapter = inject(FilterOptionsAdapter);
  private readonly filters = inject(FilterScopeService);
  private readonly session = inject(SessionService);
  private readonly clock = inject(ClockService);

  /** Lazy backing for {@link probeWindow}; dropped at the session boundary (R-019). */
  private probeWindowState: ResolvedDateRange | null = null;

  private readonly lists = signal<ReadonlyMap<string, FilterOptionList>>(new Map());
  private readonly clientNames = signal<ReadonlyMap<string, string>>(new Map());

  /** Non-reactive: guards against a second in-flight fetch for the same key. */
  private readonly inFlight = new Set<string>();
  /**
   * In-flight fetches, keyed so ONE can be aborted without touching the others — which
   * {@link invalidateClientDirectory} needs and the session-boundary clear does not care about.
   */
  private readonly controllers = new Map<string, AbortController>();

  private readonly internal = computed(() => isInternalRole(this.session.session()));

  /**
   * GH-164 D-12: the open-set dimensions the active route offers as OPERABLE selectors — neither
   * hidden nor disabled. Set by the filter bar, which is the one place that knows; empty when no
   * bar is mounted. Only a non-empty demand makes a staff caller with no client fetch cross-client.
   */
  private readonly crossClientDemand = signal<ReadonlySet<OpenSetDimension>>(new Set());

  /**
   * R-002: computed once at first use and held for the session — independent
   * of the selected range — then recomputed at the *next* session's first use
   * (FR-019/R-019), so a browser left on /sign-in overnight never carries a
   * stale window into a later login.
   */
  get probeWindow(): ResolvedDateRange {
    this.probeWindowState ??= untracked(() =>
      trailingUtcWindow(this.clock.now(), OPTION_PROBE_WINDOW_DAYS),
    );
    return this.probeWindowState;
  }

  constructor() {
    effect(() => {
      // Nothing is fetched before there is a session to fetch it with: the bar
      // is absent from /sign-in, but the service is root-provided and outlives
      // any bar instance.
      if (!this.session.isAuthenticated()) return;

      const internal = this.internal();
      const clientCode = this.filters.scope().clientCode;
      // Tracked so a route that starts offering an operable selector fetches (GH-164 D-12).
      this.crossClientDemand();

      untracked(() => this.fetchMissing(internal, clientCode));
    });

    // FR-019/R-019: a change of session *owner* aborts and clears — sign-out
    // keeps this root service alive, and the next owner must never observe the
    // previous one's option values or client names, nor have a request the old
    // owner started populate its cache.
    //
    // The signal is the ownership epoch, not the falling edge of
    // authentication: an owner can be replaced *in place* (AITH-43 permits a
    // second sign-in with no sign-out first), and `isAuthenticated` is true on
    // both sides of that — an auth-edge rule would serve the new owner the old
    // one's caches. The epoch is also what distinguishes a replacement from a
    // renewal, which refreshes the *same* owner and must keep the caches
    // (re-fetching all three lists on every token renewal contradicts FR-015).
    //
    // `hadOwner` keeps boot → first sign-in from firing: there is no previous
    // owner to clear after, and the caches are empty there anyway.
    let lastEpoch = untracked(() => this.session.sessionEpoch());
    let hadOwner = untracked(() => this.session.isAuthenticated());
    effect(() => {
      const epoch = this.session.sessionEpoch();
      const authenticated = this.session.isAuthenticated();
      const ownerChanged = epoch !== lastEpoch && hadOwner;
      lastEpoch = epoch;
      hadOwner = authenticated;
      if (!ownerChanged) return;

      untracked(() => {
        this.clearForSessionEnd();
        // …and prime the new owner here rather than waiting for the effect
        // above: an in-place replacement can leave *its* inputs identical on
        // both sides (same role class, same selected client), so nothing would
        // re-trigger it and the new owner would sit on the emptied caches.
        // Sign-out primes nothing — there is no owner to fetch with.
        if (authenticated) {
          this.fetchMissing(this.internal(), this.filters.scope().clientCode);
        }
      });
    });

    inject(DestroyRef).onDestroy(() => {
      for (const controller of this.controllers.values()) controller.abort();
      this.controllers.clear();
    });
  }

  /**
   * FR-019/R-019: abort everything in flight (an aborted fetch never settles
   * into the cache — see {@link startFetch}'s aborted check), drop both caches,
   * and drop the probe window so the next session recomputes it at first use.
   */
  private clearForSessionEnd(): void {
    for (const controller of this.controllers.values()) controller.abort();
    this.controllers.clear();
    this.inFlight.clear();
    this.lists.set(new Map());
    this.clientNames.set(new Map());
    this.probeWindowState = null;
  }

  /**
   * Starts whatever the current caches are missing. Idempotent — an already
   * cached or already in-flight key is skipped by the `ensure*` methods — so
   * both callers can run it in the same pass without duplicating a fetch.
   */
  private fetchMissing(internal: boolean, clientCode: string | null): void {
    if (internal) this.ensureClientDirectory();
    for (const dimension of OPEN_SET_DIMENSIONS) {
      this.ensureDimension(dimension, internal, clientCode);
    }
  }

  /**
   * The current list for a dimension. `loading` until the first fetch settles —
   * never `idle`, because the service fetches on first need and has no
   * pre-fetch resting state (data-model §5).
   */
  optionsFor(dimension: FilterOptionDimension): Signal<FilterOptionList> {
    return computed(() => this.lists().get(this.keyFor(dimension)) ?? LOADING_OPTION_LIST);
  }

  /** The client's display name, falling back to its code when `dim_client` has none. */
  clientDisplayName(clientCode: string): string {
    return this.clientNames().get(clientCode) ?? clientCode;
  }

  /**
   * Drops the cached client directory so the next read re-fetches it.
   *
   * **The one thing that legitimately changes the directory mid-session is a registration** (GH-29
   * SC-003: a registered client is selectable in the Client dropdown within one refresh of the
   * save). The directory is otherwise cached once per session and has no other reason to move, so
   * this is deliberately a single narrow entry point rather than a general cache-busting API —
   * nothing else may call it, and the session-boundary clear stays the only other invalidation.
   *
   * **It re-fetches here, and must.** The first version of this method only dropped the key, on the
   * belief that the constructor effect would notice and re-run. It does not: that effect tracks
   * authentication, {@link internal} and the scope's client code, and calls {@link fetchMissing}
   * inside `untracked`, so a `lists` mutation is invisible to it. Dropping the key alone therefore
   * replaced a stale-but-usable directory with one that never resolved — strictly worse than the
   * staleness it was fixing (review round 7).
   *
   * An in-flight directory read is aborted first. Without that, a response already on the wire —
   * one that predates the registration, and therefore cannot contain the new client — would settle
   * into the cache after this call and re-establish exactly the stale answer being invalidated.
   *
   * A caller with no session, or one that is not internal, gets the drop without the re-fetch:
   * those are the conditions under which {@link fetchMissing} would not have asked for the
   * directory either, and a write path must not conjure a read the bar would never make.
   */
  invalidateClientDirectory(): void {
    const controller = this.controllers.get(CLIENT_DIRECTORY_KEY);
    if (controller !== undefined) {
      controller.abort();
      this.controllers.delete(CLIENT_DIRECTORY_KEY);
    }
    this.inFlight.delete(CLIENT_DIRECTORY_KEY);

    const next = new Map(this.lists());
    next.delete(CLIENT_DIRECTORY_KEY);
    this.lists.set(next);

    if (!this.session.isAuthenticated() || !this.internal()) return;
    this.ensureClientDirectory();
  }

  /**
   * GH-164 D-12: declares which open-set dimensions the active route offers as operable selectors.
   * Called by the filter bar; an empty set (the default, and every production route in stage 2)
   * keeps the R-003 behaviour exactly.
   */
  setCrossClientDemand(dimensions: ReadonlySet<OpenSetDimension>): void {
    const current = this.crossClientDemand();
    if (current.size === dimensions.size && [...dimensions].every((dimension) => current.has(dimension))) return;
    this.crossClientDemand.set(new Set(dimensions));
  }

  private keyFor(dimension: FilterOptionDimension): string {
    if (dimension === CLIENT_DIRECTORY_KEY) return CLIENT_DIRECTORY_KEY;
    // The date is deliberately absent from the key: the probe window is fixed
    // for the session, so a date change never invalidates a list (R-002).
    const clientCode = this.filters.scope().clientCode;
    if (clientCode === null && this.crossClientDemand().has(dimension)) return this.crossClientKey(dimension);
    return `${dimension}|${clientCode ?? ''}|${this.probeWindow.fromUtc}`;
  }

  /** A distinct client-code slot, so a cross-client list can never collide with an R-003 or per-client one. */
  private crossClientKey(dimension: OpenSetDimension): string {
    return `${dimension}|${CROSS_CLIENT_SLOT}|${this.probeWindow.fromUtc}`;
  }

  private ensureDimension(
    dimension: OpenSetDimension,
    internal: boolean,
    clientCode: string | null,
  ): void {
    // GH-164 D-12: an operable selector on a page with no client — one call settles all three. Checked
    // BEFORE the per-key guard below: the R-003 answer is cached under a different key, and finding it
    // there must not stop a route that now offers a selector from fetching.
    if (internal && clientCode === null && untracked(() => this.crossClientDemand()).has(dimension)) {
      this.ensureCrossClient();
      return;
    }

    const key = `${dimension}|${clientCode ?? ''}|${this.probeWindow.fromUtc}`;
    if (this.lists().has(key) || this.inFlight.has(key)) return;

    if (internal && clientCode === null) {
      // R-003: structurally unobtainable until a client is selected. Cached
      // under this key, so selecting a client moves to a different key and
      // fetches; clearing the selection returns to this settled answer.
      this.setList(key, UNAVAILABLE_OPTION_LIST);
      return;
    }

    this.startFetch(key, (signal) =>
      this.adapter
        // A client-scoped caller MUST NOT send clientCode (403); a staff caller
        // MUST (400). `internal` is exactly that distinction.
        .loadDimensionRows(dimension, internal ? clientCode : null, this.probeWindow, signal)
        .then((result) => (result.ok
          ? toOptionList(deriveOptionValues(result.value), observesUnspecified(result.value))
          : UNAVAILABLE_OPTION_LIST)),
    );
  }

  /**
   * GH-164 S2-U1: ONE call for all three open-set lists. Shares the session machinery with
   * {@link startFetch} — one controller (so the session-boundary clear aborts it), the aborted-result
   * guard, and the no-retry rule (a failure settles all three as unavailable for the session).
   */
  private ensureCrossClient(): void {
    const keys = OPEN_SET_DIMENSIONS.map((dimension) => [dimension, this.crossClientKey(dimension)] as const);
    if (this.inFlight.has(CROSS_CLIENT_FETCH_KEY) || keys.every(([, key]) => this.lists().has(key))) return;

    this.inFlight.add(CROSS_CLIENT_FETCH_KEY);
    for (const [, key] of keys) this.setList(key, LOADING_OPTION_LIST);

    const controller = new AbortController();
    this.controllers.set(CROSS_CLIENT_FETCH_KEY, controller);

    const settleAll = (listFor: (dimension: OpenSetDimension) => FilterOptionList): void => {
      if (controller.signal.aborted) return;
      for (const [dimension, key] of keys) this.setList(key, listFor(dimension));
    };

    void this.adapter.loadFilterOptions(this.probeWindow, controller.signal)
      .then((result) => {
        if (!result.ok) {
          settleAll(() => UNAVAILABLE_OPTION_LIST);
          return;
        }
        const byDimension = {
          app: result.value.apps,
          model: result.value.models,
          provider: result.value.providers,
        } as const;
        // The server orders most-used first and the bar keeps that order: the most likely choice is
        // at the top of a list that may be long (decisions D-04).
        settleAll((dimension) => toOptionList(byDimension[dimension].values, byDimension[dimension].unspecifiedObserved));
      })
      .catch(() => settleAll(() => UNAVAILABLE_OPTION_LIST))
      .finally(() => {
        this.inFlight.delete(CROSS_CLIENT_FETCH_KEY);
        if (this.controllers.get(CROSS_CLIENT_FETCH_KEY) === controller) this.controllers.delete(CROSS_CLIENT_FETCH_KEY);
      });
  }

  private ensureClientDirectory(): void {
    if (this.lists().has(CLIENT_DIRECTORY_KEY) || this.inFlight.has(CLIENT_DIRECTORY_KEY)) return;

    this.startFetch(CLIENT_DIRECTORY_KEY, (signal) =>
      this.adapter.loadClientDirectory(signal).then((result) => {
        if (!result.ok) return UNAVAILABLE_OPTION_LIST;

        // The API orders by name then clientCode; that order is preserved
        // verbatim — no client-side re-sort (contract "Ordering").
        const names = new Map<string, string>();
        for (const entry of result.value) {
          if (entry.name !== null && entry.name !== '') names.set(entry.clientCode, entry.name);
        }

        // The name cache is a SIDE EFFECT, so it needs the same stale-result
        // guard `startFetch` applies to the returned list — which runs after
        // this callback, not before it. Aborting is best-effort: a response that
        // had already resolved when the session boundary aborted would otherwise
        // overwrite the replacement owner's names even though its list is
        // discarded (FR-019/R-019).
        if (signal.aborted) return UNAVAILABLE_OPTION_LIST;
        this.clientNames.set(names);

        return toOptionList(result.value.map((entry) => entry.clientCode));
      }),
    );
  }

  /**
   * Runs one fetch for a key and settles it exactly once.
   *
   * A failed list settles as `error` and is **not** retried for the rest of the
   * session — the literal reading of FR-015's "fetched once and cached for the
   * duration of the session". The bar stays fully functional around it
   * (FR-016), and a value restored from the URL is still applied and displayed
   * whatever the list's state (FR-006).
   */
  private startFetch(
    key: string,
    run: (signal: AbortSignal) => Promise<FilterOptionList>,
  ): void {
    this.inFlight.add(key);
    this.setList(key, LOADING_OPTION_LIST);

    const controller = new AbortController();
    this.controllers.set(key, controller);

    void run(controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return;
        this.setList(key, list);
      })
      .catch(() => {
        // The adapter already converts every failure into `ok: false`; this is
        // the belt-and-braces path so a list can never reject into the console
        // (FR-042: zero console errors).
        if (!controller.signal.aborted) this.setList(key, UNAVAILABLE_OPTION_LIST);
      })
      .finally(() => {
        this.inFlight.delete(key);
        // Only if this fetch is still the one registered for the key: a later fetch may have
        // replaced it after an invalidation, and deleting unconditionally would orphan that one.
        if (this.controllers.get(key) === controller) this.controllers.delete(key);
      });
  }

  private setList(key: string, list: FilterOptionList): void {
    this.lists.update((current) => new Map(current).set(key, list));
  }
}

/**
 * data-model §5: the grouped-usage response is metric rows, not a distinct-value
 * array — the same `dimensionValue` recurs once per metric and once per
 * currency.
 *
 * 1. keep only `dimensionState: 'specified'` rows — `unspecified` marks
 *    telemetry with no value for that dimension, not a selectable one;
 * 2. deduplicate by exact value (case-sensitive: these are open-set
 *    identifiers, not display text);
 * 3. sort ordinal ascending, so the list is deterministic across calls and
 *    independent of the response's metric ordering.
 */
export function deriveOptionValues(rows: readonly GroupedUsageRow[]): readonly string[] {
  const values = new Set<string>();
  for (const row of rows) {
    if (row.dimensionState !== DIMENSION_STATE_SPECIFIED) continue;
    values.add(row.dimensionValue);
  }
  return [...values].sort((left, right) => (left < right ? -1 : left > right ? 1 : 0));
}

/** GH-164: whether a grouped-usage response carried telemetry with no value for the dimension. */
function observesUnspecified(rows: readonly GroupedUsageRow[]): boolean {
  return rows.some((row) => row.dimensionState !== DIMENSION_STATE_SPECIFIED);
}

/**
 * An obtained-but-empty list is `empty`; only an unobtainable one is `error` (FR-016). A list whose
 * only observation is the blank segment is `success` (GH-164): the Unspecified choice is selectable.
 */
function toOptionList(values: readonly string[], unspecifiedObserved = false): FilterOptionList {
  if (values.length === 0 && !unspecifiedObserved) return EMPTY_OPTION_LIST;
  return unspecifiedObserved ? { status: 'success', values, unspecifiedObserved } : { status: 'success', values };
}
